import React, { useContext, useMemo, useRef, useState } from 'react';
import fromPairs from 'lodash-es/fromPairs';
import functions from 'lodash-es/functions';

export function createContextHelper<A extends unknown | null>() {
  const ctx = React.createContext<A | undefined>(undefined);

  function useCtx() {
    const contextValue = useContext(ctx);

    if (contextValue === undefined) {
      throw new Error('Must be used inside a provider');
    }

    return contextValue;
  }

  return [useCtx, ctx.Provider] as const;
}

type RefContextType<E> = {
  getValue: () => E | null;
  setValue: (value: E) => void;
};
export function createRefContextHelper<A>() {
  const ctx = React.createContext<RefContextType<A> | undefined>(undefined);

  function useCtx() {
    const contextValue = useContext(ctx);
    const [counter, setCounter] = useState(0);

    if (contextValue === undefined) {
      throw new Error('Must be used inside a provider');
    }

    const setContextValue = (value: A) => {
      contextValue.setValue(value);

      if (!counter) {
        setCounter(1);
      }
    };

    return [contextValue.getValue(), setContextValue] as const;
  }

  return [useCtx, ctx.Provider] as const;
}

type HandlersContextType<T> = {
  handlers: T;
  handlersRef: React.RefObject<T>;
};
export function createHandlersContextHelper<T>(initHandlers: () => T) {
  const ctx = React.createContext<HandlersContextType<T> | undefined>(
    undefined
  );
  const initialHandlers = initHandlers();

  function useCtx() {
    const contextValue = useContext(ctx);

    if (contextValue === undefined) {
      throw new Error('Must be used inside a provider');
    }

    return contextValue;
  }

  function useContextValue() {
    // The idea here is that you can attach to `handlers` and it will never update, but allows
    // for the reference methods to change. Originally there was only a reference but for any
    // place that passed a method from the handlers, it could break as it only had a reference
    // which does not trigger a re-render.
    const handlersRef = useRef<T>(initialHandlers);
    const pointerObject = useMemo(
      () =>
        fromPairs(
          functions(handlersRef.current).map(fnName => [
            fnName,
            (...args: any) => (handlersRef.current as any)[fnName](...args),
          ])
        ) as unknown as T,
      []
    );
    const pointerRef = useRef<T>(pointerObject);
    const contextValue = useMemo(() => {
      return {
        handlers: pointerRef.current,
        handlersRef,
      };
    }, []);

    return contextValue;
  }

  return [useCtx, ctx.Provider, useContextValue] as const;
}
